import { getCoreRowModel, getExpandedRowModel } from "@tanstack/react-table"
import type { ExpandedState, OnChangeFn, SortingState } from "@tanstack/react-table"
import type { FC } from "react"

import { useApAgingVendorColumns } from "../hooks/useApAgingVendorColumns"
import type { ApAgingVendor } from "../types/apAgingReport"

import { ApAgingVendorRowDetails } from "./ApAgingVendorRowDetails"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { expandedCellStyles } from "@/components/Tables/Aeros/shared"

interface ApAgingVendorTableProps {
  data: ApAgingVendor[]
  invoiceSorting: SortingState
  onInvoiceSortingChange: OnChangeFn<SortingState>
  expanded: ExpandedState
  onExpandedChange: OnChangeFn<ExpandedState>
  loading: boolean
  error?: { message: string }
}

// AK-6964 AC5: One row per vendor; AC6: each row expands into the invoices behind its totals.
export const ApAgingVendorTable: FC<ApAgingVendorTableProps> = ({
  data,
  invoiceSorting,
  onInvoiceSortingChange,
  expanded,
  onExpandedChange,
  loading,
  error,
}) => {
  const columns = useApAgingVendorColumns()

  return (
    <AerosBaseTable<ApAgingVendor>
      error={error}
      features={{
        table: { stickyHeader: true },
        tanstackOptions: {
          data,
          columns,
          state: { isLoading: loading, expanded },
          enableSorting: false,
          getCoreRowModel: getCoreRowModel(),
          getExpandedRowModel: getExpandedRowModel(),
          getRowId: (vendor) => vendor.vendorCompanyId,
          getRowCanExpand: () => true,
          onExpandedChange,
          renderExpandedContent: (row) => (
            <ApAgingVendorRowDetails
              row={row}
              invoiceSorting={invoiceSorting}
              onInvoiceSortingChange={onInvoiceSortingChange}
            />
          ),
        },
      }}
      slotProps={{
        headerCell: { className: "px-3 whitespace-nowrap" },
        bodyCell: { className: "px-3 whitespace-nowrap tabular-nums" },
        container: {
          className: "overflow-auto",
          ...({ "data-testid": "ap-aging-report.vendor-table.table" } as Record<string, string>),
        },
        bodyRow: { className: "hover:bg-gray-50" },
        bodyCellExpanded: { className: "bg-primary-100" },
        expandedCell: expandedCellStyles,
      }}
      noDataMessage='No vendors found'
    />
  )
}
